/** @format */

import GlobeIcon from '@/assets/icons/dovevivo_icon_globe.svg';
import GlobeIconLight from '@/assets/icons/dovevivo_icon_globe_light.svg';
import { TOtherPaths } from '@/ducks/filters/declarations';
import { selectorCurrentPage } from '@/ducks/general/selectors';
import { GTMheader } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { useAppSelector } from '@/redux/hooks';
import { TLocale } from '@/utils/gatsby/types';
import { utilityGetLocaleURL } from '@/utils/helpers/getLocaleURL';
import { IntlContextConsumer } from 'gatsby-plugin-intl';
import React, { memo, useMemo, useState } from 'react';
import styled from 'styled-components';

interface Props {
  otherPaths: TOtherPaths;
  home?: boolean;
}

export const UISwitchLocale = memo(({ otherPaths, home = false }: Props) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const currentPage = useAppSelector(selectorCurrentPage);

  const currentParams = () => {
    const params = new URLSearchParams(window.location.search);
    const paramsObj: { [key: string]: string } = {};
    params.forEach((value, key) => {
      paramsObj[key] = value;
    });
    return paramsObj;
  };

  const hasQueryParams =
    currentPage === 'listing' ||
    currentPage === 'booking' ||
    currentPage === 'request-visit' ||
    currentPage === 'request-info' ||
    currentPage === 'request-info-received' ||
    currentPage === 'request-visit-confirmed';

  function updateUrlWithParams(base: string, newParams: { [key: string]: string }) {
    const url = new URL(base);
    const searchParams = new URLSearchParams(url.search);

    Object.entries(newParams).forEach(([key, value]) => {
      searchParams.set(key, value);
    });

    url.search = searchParams.toString();
    return url.toString();
  }

  const handleClick = (locale: TLocale) => {
    document.location.href = hasQueryParams
      ? updateUrlWithParams(utilityGetLocaleURL(locale, otherPaths), currentParams())
      : utilityGetLocaleURL(locale, otherPaths);
    GTMheader('language', locale);
  };

  const GlobeSrc = useMemo(() => {
    if (home) return GlobeIconLight;
    return GlobeIcon;
  }, [home]);

  return (
    <IntlContextConsumer>
      {({ languages, language }) => (
        <SwitchLocaleContainer>
          <ContainerButtonImg onClick={() => setShowOptions(!showOptions)}>
            <img alt="change locale" src={GlobeSrc} />
          </ContainerButtonImg>
          {showOptions && <ContainerOptions onClick={() => setShowOptions(false)} />}
          <ContainerLanguages showOptions={showOptions}>
            {languages.map(locale => (
              <LanguageItem key={locale}>
                {locale === language ? (
                  <DisabledLanguage>{locale.toUpperCase()}</DisabledLanguage>
                ) : (
                  <LanguageButton type="button" onClick={() => handleClick(locale as TLocale)}>
                    {locale.toUpperCase()}
                  </LanguageButton>
                )}
              </LanguageItem>
            ))}
          </ContainerLanguages>
        </SwitchLocaleContainer>
      )}
    </IntlContextConsumer>
  );
});

// #region ::: COMPONENTS
interface PropsLanguages {
  showOptions: boolean;
}

const SwitchLocaleContainer = styled.div`
  position: 'relative';
`;

const ContainerLanguages = styled.div<PropsLanguages>`
  display: ${({ showOptions }) => (showOptions ? 'block' : 'none')};
  position: absolute;
  background-color: ${({ theme }) => theme.colors.joivy.white};
  border: 1px solid ${({ theme }) => theme.colors.joivy['teal-dark']};

  top: 40px;
  right: 0;
  z-index: 2;
`;

const LanguageItem = styled.div`
  text-align: center;
  padding: 2px 12px;
  width: 100%;
`;

const ContainerButtonImg = styled.button`
  border: none;
  background-color: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0;
`;

const LanguageButton = styled.button`
  border: none;
  color: ${({ theme }) => theme.colors.joivy['teal-dark']};
  background-color: transparent;
  cursor: pointer;
  font-size: 18px;
  padding: 0;

  &:hover {
    animation: fweight 0.1s ease forwards;
  }

  @keyframes fweight {
    from {
      font-weight: 400;
    }
    to {
      font-weight: 500;
    }
  }
`;

const DisabledLanguage = styled.div`
  color: ${({ theme }) => theme.colors.lightGray};
  cursor: not-allowed;
  font-size: 18px;
  font-weight: 700;
`;

const ContainerOptions = styled.button`
  border: none;
  background-color: transparent;
  z-index: 1;
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
`;

// #endregion
